import { Trans, useLingui } from '@lingui/react/macro'
import CantileverButton from 'components/CantileverButton'
import {
  formatMdCaseDateTimeRange,
  getSeverityColor,
} from 'features/mdCases/mdCaseDisplay'
import React from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { HoverScrollableText } from './HoverScrollableText'
import type {
  OverlayAlert,
  PositionedOverlayAlert,
} from './mdDraftAlertCaseFlowTypes'

const SeverityDot = styled.span<{ $color: string }>`
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.18);
  flex-shrink: 0;
`

const OverlayBand = styled.div<{
  $left: number
  $top: number
  $width: number
  $height: number
  $background: string
  $borderColor: string
}>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  background: ${({ $background }) => $background};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  pointer-events: none;
`

const OverlayBadge = styled.button<{
  $left: number
  $top: number
  $background: string
  $borderColor: string
}>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  z-index: 8;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  max-width: 12rem;
  min-width: 0;
  padding: 0.25rem 0.45rem;
  border-radius: 999px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  background: ${({ $background }) => $background};
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.1;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    filter: brightness(1.05);
  }
`

const OverlayBadgeLabel = styled(HoverScrollableText)`
  flex: 1;
  min-width: 0;
`

const DetailCard = styled.div<{
  $left: number
  $top: number
}>`
  position: absolute;
  left: ${({ $left }) => `${$left}px`};
  top: ${({ $top }) => `${$top}px`};
  z-index: 12;
  width: min(14rem, calc(100vw - 2rem));
  display: grid;
  gap: 0.35rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.65rem;
  border: 1px solid ${({ theme }) => theme.forecast.glassBackground};
  background: rgba(9, 12, 20, 0.94);
  color: ${({ theme }) => theme.forecast.defaultText};
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.24);

  > strong {
    font-weight: 700;
    font-size: 0.875rem;
  }

  > small {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  > p {
    margin: 0;
    font-size: 0.8125rem;
    white-space: pre-wrap;
  }

  > div[data-actions='true'] {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
`

type SensorLike = {
  id: string
  data: {
    name?: string | null
  }
}

export interface MdDraftAlertCaseFlowPanelsProps {
  chartAreaRef: React.MutableRefObject<HTMLDivElement | null>
  positionedAlerts: readonly PositionedOverlayAlert[]
  activePositionedAlert: PositionedOverlayAlert | null
  sensorsById: Map<string, SensorLike>
  isDeletingAlertId: string | null
  allowSavedAlertDelete: boolean
  onDeleteAlert: (alert: OverlayAlert) => Promise<void>
  onOpenCase: (caseId: string | null) => void
  onStartHoverAlert: (alertId: string) => void
  onScheduleHoverAlertHide: (alertId: string) => void
  onToggleAlert: (alertId: string) => void
  dismissedAlertIds: ReadonlySet<string>
}

export const MdDraftAlertCaseFlowPanels: React.FC<
  MdDraftAlertCaseFlowPanelsProps
> = ({
  chartAreaRef,
  positionedAlerts,
  activePositionedAlert,
  sensorsById,
  isDeletingAlertId,
  allowSavedAlertDelete,
  onDeleteAlert,
  onOpenCase,
  onStartHoverAlert,
  onScheduleHoverAlertHide,
  onToggleAlert,
  dismissedAlertIds,
}) => {
  const { t } = useLingui()

  const getSensorName = (sensorId: string) =>
    sensorsById.get(sensorId)?.data.name || sensorId

  const overlayHost = chartAreaRef.current
  const shouldPortal = overlayHost?.isConnected ?? false

  const overlayContent = (
    <>
      {positionedAlerts.map((alert) => {
        const color = getSeverityColor(alert.severity)
        const badgeLabel = alert.title || getSensorName(alert.primarySensorId)
        const isDismissed = dismissedAlertIds.has(alert.id)

        return (
          <React.Fragment key={alert.id}>
            {!isDismissed && (
              <OverlayBand
                $left={alert.focusLeft}
                $top={alert.plotTop}
                $width={alert.focusWidth}
                $height={alert.plotHeight}
                $background={`${color}38`}
                $borderColor={color}
              />
            )}
            <OverlayBadge
              type='button'
              $left={alert.badgeLeft}
              $top={alert.badgeTop}
              $background={`${color}cc`}
              $borderColor={color}
              style={isDismissed ? { opacity: 0.45 } : undefined}
              title={
                isDismissed ? t`Click to restore alert highlight` : undefined
              }
              onMouseEnter={
                isDismissed ? undefined : () => onStartHoverAlert(alert.id)
              }
              onMouseLeave={
                isDismissed
                  ? undefined
                  : () => onScheduleHoverAlertHide(alert.id)
              }
              onClick={() => onToggleAlert(alert.id)}>
              {alert.severity && <SeverityDot $color={color} />}
              <Trans>Alert</Trans>
              {' · '}
              <OverlayBadgeLabel text={badgeLabel} />
            </OverlayBadge>
          </React.Fragment>
        )
      })}

      {activePositionedAlert && (
        <DetailCard
          $left={activePositionedAlert.detailLeft}
          $top={activePositionedAlert.detailTop}
          data-testid='manual-alert-detail-card'
          onMouseEnter={() => onStartHoverAlert(activePositionedAlert.id)}
          onMouseLeave={() =>
            onScheduleHoverAlertHide(activePositionedAlert.id)
          }>
          <strong>
            {activePositionedAlert.title ||
              getSensorName(activePositionedAlert.primarySensorId)}
          </strong>
          <small>
            <Trans>Saved alert</Trans>
            {' · '}
            {activePositionedAlert.severity ?? t`Severity not set`}
          </small>
          <small>
            <Trans>Range:</Trans>{' '}
            {formatMdCaseDateTimeRange(
              activePositionedAlert.focusStartAt,
              activePositionedAlert.focusEndAt
            )}
          </small>
          {activePositionedAlert.summary &&
            activePositionedAlert.summary !== activePositionedAlert.title && (
              <p>{activePositionedAlert.summary}</p>
            )}
          {(allowSavedAlertDelete || activePositionedAlert.caseId) && (
            <div data-actions='true'>
              {allowSavedAlertDelete && (
                <CantileverButton
                  ghost
                  danger
                  onClick={() => void onDeleteAlert(activePositionedAlert)}
                  disabled={isDeletingAlertId === activePositionedAlert.id}>
                  {isDeletingAlertId === activePositionedAlert.id
                    ? t`Deleting...`
                    : t`Delete`}
                </CantileverButton>
              )}
              {activePositionedAlert.caseId && (
                <CantileverButton
                  ghost
                  onClick={() => onOpenCase(activePositionedAlert.caseId)}>
                  <Trans>Open Case</Trans>
                </CantileverButton>
              )}
            </div>
          )}
        </DetailCard>
      )}
    </>
  )

  return shouldPortal && overlayHost
    ? createPortal(overlayContent, overlayHost)
    : overlayContent
}
